import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { NotificationService } from '../../services/notification.service';
import { CanaisVendaFacade } from '../../canais-venda/canais-venda.facade';
import { LibraryGroupVM, ProductCatalogItemVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';

type AdditionalCatalogFilterV2 = 'all' | 'exclusive' | 'shared' | 'unlinked';

@Component({
  selector: 'app-tela-catalogo-adicionais-v2',
  templateUrl: './tela-catalogo-adicionais-v2.component.html',
  styleUrls: ['./tela-catalogo-adicionais-v2.component.scss'],
  providers: [CanaisVendaFacade]
})
export class TelaCatalogoAdicionaisV2Component implements OnInit, OnDestroy {
  empresa: any;
  loading = true;
  errorMessage = '';
  search = '';
  filter: AdditionalCatalogFilterV2 = 'all';
  items: LibraryGroupVM[] = [];
  products: ProductCatalogItemVM[] = [];
  temCanalVendaAtivo = false;
  precosCanaisAberto = false;
  adicionalPrecosCanalId: number | null = null;

  private empresaSubscription?: Subscription;
  private restoreScrollTimer?: ReturnType<typeof setTimeout>;
  private loadSequence = 0;
  private canaisSequence = 0;

  constructor(
    private constantsService: ConstantsService,
    private facade: ProdutosV2FacadeService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private elementRef: ElementRef<HTMLElement>,
    private canaisVenda: CanaisVendaFacade
  ) {}

  ngOnInit(): void {
    this.empresaSubscription = this.constantsService.empresa$.subscribe((empresa) => {
      if (!empresa?.catalogo?.id) return;
      const catalogoMudou = this.empresa?.id !== empresa.id || this.empresa?.catalogo?.id !== empresa.catalogo.id;
      this.empresa = empresa;
      const state = this.facade.additionalCatalogState;
      const restore = state?.empresaId === empresa.id && state?.catalogoId === empresa.catalogo.id;
      this.search = restore ? state.search : '';
      this.filter = restore ? state.filter : 'all';
      this.items = restore ? state.items : [];
      this.products = restore ? state.products : [];
      if (catalogoMudou) {
        this.fechePrecosCanais();
        this.temCanalVendaAtivo = false;
        void this.carregueCanaisAtivos(empresa);
      }
      clearTimeout(this.restoreScrollTimer);
      if (restore) {
        this.restoreScrollTimer = setTimeout(() => {
          this.scrollElements().forEach((element, index) => element.scrollTop = state.scrollPositions[index] || 0);
        });
      }
      void this.load(restore);
    });
  }

  ngOnDestroy(): void {
    this.empresaSubscription?.unsubscribe();
    clearTimeout(this.restoreScrollTimer);
    this.loadSequence++;
    this.canaisSequence++;
  }

  get canEdit(): boolean {
    return !this.empresa?.id || !this.empresa?.catalogo?.compartilhado;
  }

  get exclusiveCount(): number {
    return this.items.filter((item) => item.sharedCount === 1).length;
  }

  get sharedCount(): number {
    return this.items.filter((item) => item.sharedCount > 1).length;
  }

  get unlinkedCount(): number {
    return this.items.filter((item) => item.sharedCount === 0).length;
  }

  get filteredItems(): LibraryGroupVM[] {
    const term = this.normalize(this.search);
    return this.items.filter((item) => {
      const matchesTerm = !term
        || this.normalize(item.name).includes(term)
        || this.normalize(item.description).includes(term)
        || (item.sharedProductNames || []).some((name) => this.normalize(name).includes(term));
      if (!matchesTerm) return false;
      if (this.filter === 'exclusive') return item.sharedCount === 1;
      if (this.filter === 'shared') return item.sharedCount > 1;
      if (this.filter === 'unlinked') return item.sharedCount === 0;
      return true;
    });
  }

  async load(background = false): Promise<void> {
    const sequence = ++this.loadSequence;
    const empresa = this.empresa;
    this.loading = !background;
    this.errorMessage = '';
    try {
      const products = await this.facade.carregueCatalogo(empresa);
      const items = await this.facade.carregueBiblioteca(empresa.catalogo);
      if (sequence !== this.loadSequence) return;
      this.products = products;
      this.items = items;
    } catch (error: any) {
      if (sequence !== this.loadSequence) return;
      this.errorMessage = error?.message || 'Não foi possível carregar os adicionais do catálogo.';
    } finally {
      if (sequence === this.loadSequence) this.loading = false;
    }
  }

  setFilter(filter: AdditionalCatalogFilterV2): void {
    this.filter = filter;
  }

  openAdditional(item: LibraryGroupVM): void {
    this.saveListState();
    void this.router.navigate(['/admin/adicionais-v2', item.id], {
      queryParams: { produto: item.sourceProductId || item.productId || null }
    });
  }

  abraPrecosCanais(item: LibraryGroupVM): void {
    this.adicionalPrecosCanalId = Number(item.id);
    this.precosCanaisAberto = true;
  }

  fechePrecosCanais(): void {
    this.precosCanaisAberto = false;
    this.adicionalPrecosCanalId = null;
  }

  createAdditional(): void {
    if (!this.canEdit) {
      this.notificationService.showError('A estrutura dos adicionais é controlada pelo catálogo da rede.');
      return;
    }
    this.saveListState();
    void this.router.navigate(['/admin/adicionais-v2/novo']);
  }

  /** Quem chegou aqui de dentro de um produto volta para ele, não para a lista. */
  get produtoDeOrigem(): number | null {
    const id = Number(this.route.snapshot.queryParams['voltarProduto'] || 0);
    return id > 0 ? id : null;
  }

  goToProducts(): void {
    const produto = this.produtoDeOrigem;
    if (!produto) {
      void this.router.navigate(['/admin/produtos-v2']);
      return;
    }

    void this.router.navigate(['/admin/produtos-v2'], {
      queryParams: {
        produtoDrawer: produto,
        drawerTab: this.route.snapshot.queryParams['voltarAba'] || 'adicionais'
      }
    });
  }

  usageLabel(item: LibraryGroupVM): string {
    if (item.sharedCount === 0) return 'Sem vínculo';
    if (item.sharedCount === 1) return 'Exclusivo';
    return `Compartilhado · ${item.sharedCount} produtos`;
  }

  usageSupport(item: LibraryGroupVM): string {
    const names = item.sharedProductNames || [];
    if (!names.length) return item.sharedCount === 0 ? 'Ainda não usado em produtos' : 'Produto não identificado';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  requirementLabel(item: LibraryGroupVM): string {
    return Number(item.min || 0) > 0 ? 'Obrigatório' : 'Opcional';
  }

  trackByAdditional(index: number, item: LibraryGroupVM): any {
    return item.id || index;
  }

  private async carregueCanaisAtivos(empresa: any): Promise<void> {
    const sequence = ++this.canaisSequence;
    try {
      const canais = await this.canaisVenda.listeCanais();
      if (sequence !== this.canaisSequence) return;
      this.temCanalVendaAtivo = canais.some((canal) => canal.ativo && Number(canal.catalogoId) === Number(empresa.catalogo.id));
    } catch {
      if (sequence === this.canaisSequence) this.temCanalVendaAtivo = false;
    }
  }

  private saveListState(): void {
    this.facade.additionalCatalogState = {
      empresaId: this.empresa.id,
      catalogoId: this.empresa.catalogo.id,
      search: this.search,
      filter: this.filter,
      items: this.items,
      products: this.products,
      scrollPositions: this.scrollElements().map((element) => element.scrollTop)
    };
  }

  private scrollElements(): HTMLElement[] {
    const elements: HTMLElement[] = [];
    let element = this.elementRef.nativeElement.parentElement;
    while (element) {
      elements.push(element);
      element = element.parentElement;
    }
    return elements;
  }

  private normalize(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
